import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { ApiError, api } from './api';
import { t } from './i18n';

export type AlertRule = 'wrong_line' | 'off_route' | 'standing' | 'no_signal' | 'line_no_bus';
export const ALERT_RULES: AlertRule[] = ['wrong_line', 'off_route', 'standing', 'no_signal', 'line_no_bus'];

export interface StaffAlert {
  _id: string;
  rule: AlertRule;
  status: 'open' | 'resolved';
  subject: { kind: 'bus' | 'line'; id: string; name: string };
  vars: Record<string, string>;
  since: string;
  opened_at: string | null;
  resolved_at: string | null;
}

export interface AlertSettings {
  enabled: boolean;
  rules: Record<AlertRule, { on: boolean; minutes: number }>;
  service_from: string;
  service_to: string;
  emails: string[];
  telegram_chat_id: string;
  notify_resolved: boolean;
  language: 'sq' | 'en';
  test_requested_at: string | null;
  test_result: { at: string; ok: boolean; message: string } | null;
}

/** The same templates the worker sends (it has the Albanian ones too). */
export const alertText = (a: Pick<StaffAlert, 'rule' | 'vars'>) => {
  switch (a.rule) {
    case 'wrong_line':
      return t('Bus {bus} is assigned to {own} but drives {other}.', a.vars);
    case 'off_route':
      return t('Bus {bus} ({own}) is off its route.', a.vars);
    case 'standing':
      return t('Bus {bus} ({own}) has not moved, away from the terminus.', a.vars);
    case 'no_signal':
      return t('No position from bus {bus} ({own}).', a.vars);
    case 'line_no_bus':
      return t('Line {line} has no bus in service.', a.vars);
  }
};

export const RULE_LABEL: Record<AlertRule, string> = {
  wrong_line: t('Bus on the wrong line'),
  off_route: t('Bus off its route'),
  standing: t('Bus standing still (away from the terminus)'),
  no_signal: t('No position from a bus'),
  line_no_bus: t('Line without a bus in service'),
};

export function useAlerts(view: 'active' | 'recent') {
  return useQuery({
    queryKey: ['alerts', view],
    queryFn: () => api<{ items: StaffAlert[] }>('/alerts', { query: { view } }),
    refetchInterval: 30_000,
  });
}

export function useAlertCount() {
  return useQuery({
    queryKey: ['alerts', 'summary'],
    queryFn: () => api<{ open: number }>('/alerts/summary'),
    refetchInterval: 60_000,
  });
}

export function useAlertSettings(enabled: boolean) {
  return useQuery({
    queryKey: ['alerts', 'settings'],
    queryFn: () =>
      api<{ settings: AlertSettings; channels: { email: boolean; telegram: boolean } }>('/settings/alerts'),
    enabled,
    // While a test message is on its way, look for the worker's answer.
    refetchInterval: (q) => {
      const s = q.state.data?.settings;
      const waiting = s?.test_requested_at && (!s.test_result || s.test_result.at < s.test_requested_at);
      return waiting ? 5_000 : false;
    },
  });
}

export function useSaveAlertSettings() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (s: Omit<AlertSettings, 'test_requested_at' | 'test_result'>) =>
      api('/settings/alerts', { method: 'PUT', body: s }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['alerts'] });
      toast.success(t('Saved'));
    },
    onError: (err) => toast.error(err instanceof ApiError ? err.message : t('Could not save')),
  });
}

export function useTestAlert() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api('/settings/alerts/test', { method: 'POST' }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['alerts', 'settings'] });
      toast.success(t('Test requested: it is sent within a minute.'));
    },
    onError: (err) => toast.error(err instanceof ApiError ? err.message : t('Could not save')),
  });
}
